import { Card, Typography, Modal, Spin, Row, Col } from "antd";
import { useEffect, useState, useMemo } from "react";
import GETConsultarStock from "../../helpers/consultar-stock/GETConsultarStock";
import { isEmptyArray } from "../../utils/isEmptyArray";
import { getCardStyle } from "../../utils/getCardStyle";

const { Text } = Typography;

const ModalStock = ({ open, onClose, sku, darkMode }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatSku = (sku) => {
    if (typeof sku !== "string") return sku;
    const clean = sku.trim();
    if (clean.length === 8) {
      return `${clean.slice(0, 6)}-${clean.slice(6)}`;
    }
    return clean;
  };

  const esItemValido = (item) => {
    return (
      item &&
      typeof item.idartl === "string" &&
      item.idartl.trim() !== "" &&
      typeof item.codigo === "string" &&
      item.codigo.trim() !== ""
    );
  };

  useEffect(() => {
    if (!open || !sku) return;

    const fetchStock = async () => {
      setLoading(true);
      try {
        const response = await GETConsultarStock(sku.trim());
        const datos = Array.isArray(response?.data) ? response.data : [];
        const itemsValidos = datos.filter(esItemValido);
        setItems(itemsValidos);
      } catch (error) {
        console.error("Error al cargar stock en modal:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, [open, sku]);

  const todosMismoArticulo = useMemo(() => {
    return (
      items.length > 0 && items.every((item) => item.idartl === items[0].idartl)
    );
  }, [items]);

  const totales = useMemo(() => {
    if (!todosMismoArticulo) return null;
    return items.reduce(
      (acc, item) => {
        acc.ctdcmp += item.ctdcmp || 0;
        acc.ctdret += item.ctdret || 0;
        acc.ctdpik += item.ctdpik || 0;
        acc.ctddis += item.ctddis || 0;
        acc.ctdplt += item.ctdplt || 0;
        acc.ctdfis += item.ctdfis || 0;
        return acc;
      },
      { ctdcmp: 0, ctdret: 0, ctdpik: 0, ctddis: 0, ctdplt: 0, ctdfis: 0 },
    );
  }, [items, todosMismoArticulo]);

  const nombreArticulo = items.length > 0 ? items[0].nombre : null;
  const skuFormateado = formatSku(sku);

  const modalTitle = nombreArticulo
    ? `${skuFormateado} — ${nombreArticulo}`
    : `Stock: ${skuFormateado}`;

  // Nodo custom para evitar ruptura visual del title del modal

  const modalTitleNode = (
    <div
      style={{
        maxWidth: "calc(100% - 40px)",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.2,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        fontWeight: 600,
      }}
      title={modalTitle}
    >
      {modalTitle}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={modalTitleNode}
      centered
      styles={{
        content: { padding: 16 },
        body: {
          padding: 0,
          maxHeight: "70vh",
          overflowY: "auto",
        },
        header: {
          backgroundColor: darkMode ? "#1f1f1f" : "#fff",
          color: darkMode ? "#fff" : "#000",
        },
      }}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ padding: "8px" }}>
          {isEmptyArray(items) ? (
            <Card
              style={{
                textAlign: "center",
                backgroundColor: darkMode ? "#1f1f1f" : "#fafafa",
                padding: "24px",
              }}
            >
              <Text type="secondary">
                No hay stock registrado para este artículo.
              </Text>
            </Card>
          ) : (
            <>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {items.map((item, index) => (
                  <Card
                    key={`${item.idartp}-${index}`}
                    styles={{ body: { padding: "12px" } }}
                    style={{
                      ...getCardStyle(darkMode),
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {/* Etiqueta de ubicación */}
                      <Text
                        strong
                        style={{
                          fontSize: "16px",
                          color: darkMode ? "#ddd" : "#333",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.etique}
                      </Text>

                      {/* Stats: cmp, ret, pik, etc. */}
                      <Row
                        gutter={[8, 8]}
                        style={{
                          marginTop: "8px",
                          paddingTop: "8px",
                          borderTop: `1px solid ${darkMode ? "#444" : "#eee"}`,
                        }}
                      >
                        {[
                          { label: "cmp", value: item.ctdcmp },
                          { label: "ret", value: item.ctdret },
                          { label: "pik", value: item.ctdpik },
                          { label: "dis", value: item.ctddis },
                          { label: "plt", value: item.ctdplt },
                          { label: "fis", value: item.ctdfis },
                        ].map((stat) => (
                          <Col key={stat.label} xs={8} sm={4} md={4}>
                            <Text
                              type="secondary"
                              style={{
                                fontSize: "16px",
                                color: darkMode ? "#ccc" : "#555",
                                display: "block",
                                textAlign: "center",
                              }}
                            >
                              {stat.label}: {Math.round(stat.value)}
                            </Text>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Totales */}
              {todosMismoArticulo && totales && (
                <Card
                  style={{
                    ...getCardStyle(darkMode),
                    backgroundColor: darkMode ? "#303030" : "#f0f2f5",
                    marginTop: "12px",
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: "17px",
                      color: darkMode ? "#fff" : "#000",
                      display: "block",
                      textAlign: "center",
                      marginBottom: "8px",
                    }}
                  >
                    Totales
                  </Text>

                  <Row gutter={[8, 8]}>
                    {[
                      { label: "cmp", value: totales.ctdcmp },
                      { label: "ret", value: totales.ctdret },
                      { label: "pik", value: totales.ctdpik },
                      { label: "dis", value: totales.ctddis },
                      { label: "plt", value: totales.ctdplt },
                      { label: "fis", value: totales.ctdfis },
                    ].map((stat) => (
                      <Col key={stat.label} xs={8} sm={4} md={4}>
                        <Text
                          strong
                          style={{
                            fontSize: "16px",
                            color: darkMode ? "#ddd" : "#333",
                            display: "block",
                            textAlign: "center",
                          }}
                        >
                          {stat.label}: {Math.round(stat.value)}
                        </Text>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ModalStock;
